import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent } from "@/components/ui/card";
import { 
  getQuestionnaireType, 
  QUESTIONNAIRE_CONFIGS, 
  type QuestionnaireConfig,
  type Question,
  type QuestionnaireType
} from "@/data/welcomeQuestionnaireData";

interface WelcomeQuestionnaireProps {
  serviceTitle: string;
  responses: Record<string, any>;
  onResponsesChange: (responses: Record<string, any>) => void;
}

export function WelcomeQuestionnaire({ 
  serviceTitle, 
  responses, 
  onResponsesChange 
}: WelcomeQuestionnaireProps) {
  const [questionnaireType, setQuestionnaireType] = useState<QuestionnaireType>('naturopathy_adults');
  const [config, setConfig] = useState<QuestionnaireConfig>(QUESTIONNAIRE_CONFIGS.naturopathy_adults);

  useEffect(() => {
    const type = getQuestionnaireType(serviceTitle);
    setQuestionnaireType(type);
    setConfig(QUESTIONNAIRE_CONFIGS[type]);
  }, [serviceTitle]);

  const handleCheckboxChange = (questionId: string, optionId: string, checked: boolean) => {
    const current = responses[questionId] || [];
    const currentArray = Array.isArray(current) ? current : [];
    
    let updated: string[];
    if (checked) {
      updated = [...currentArray, optionId];
    } else {
      updated = currentArray.filter((id: string) => id !== optionId);
    }
    
    onResponsesChange({ ...responses, [questionId]: updated });
  };

  const handleTextChange = (questionId: string, value: string | { day: string; month: string; year: string }) => {
    onResponsesChange({ ...responses, [questionId]: value });
  };

  const handleScaleChange = (questionId: string, value: number) => {
    onResponsesChange({ ...responses, [questionId]: value });
  };

  const handleConditionalTextChange = (questionId: string, selected: string, detail?: string) => {
    onResponsesChange({ 
      ...responses, 
      [questionId]: selected,
      [`${questionId}_detail`]: detail || ''
    });
  };

  const renderQuestion = (question: Question) => {
    switch (question.type) {
      case 'checkbox':
        return (
          <div className="space-y-2">
            {question.options?.map((option) => {
              const currentValues = responses[question.id] || [];
              const isChecked = Array.isArray(currentValues) 
                ? currentValues.includes(option.id) 
                : false;
              
              return (
                <div key={option.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={`${question.id}_${option.id}`}
                    checked={isChecked}
                    onCheckedChange={(checked) => 
                      handleCheckboxChange(question.id, option.id, checked as boolean)
                    }
                  />
                  <label 
                    htmlFor={`${question.id}_${option.id}`} 
                    className="text-sm cursor-pointer"
                  >
                    {option.label}
                  </label>
                </div>
              );
            })}
          </div>
        );

      case 'text':
        // Check if this is a conditional "Autre" field
        const isAutreField = question.id.endsWith('_autre');
        if (isAutreField) {
          const parentQuestionId = question.id.replace('_autre', '');
          const parentValues = responses[parentQuestionId] || [];
          const showField = Array.isArray(parentValues) && parentValues.includes('autre');
          
          if (!showField) return null;
        }
        
        return (
          <Textarea
            id={question.id}
            value={responses[question.id] || ''}
            onChange={(e) => handleTextChange(question.id, e.target.value)}
            placeholder={question.placeholder}
            rows={3}
          />
        );

      case 'date':
        const dateValue = responses[question.id] || { day: '', month: '', year: '' };
        return (
          <div className="grid grid-cols-3 gap-2">
            <Input
              type="number"
              placeholder="Jour"
              min="1"
              max="31"
              value={dateValue.day || ''}
              onChange={(e) => handleTextChange(question.id, { ...dateValue, day: e.target.value })}
            />
            <Input
              type="number"
              placeholder="Mois"
              min="1"
              max="12"
              value={dateValue.month || ''}
              onChange={(e) => handleTextChange(question.id, { ...dateValue, month: e.target.value })}
            />
            <Input
              type="number"
              placeholder="Année"
              min="1900"
              max={new Date().getFullYear()}
              value={dateValue.year || ''}
              onChange={(e) => handleTextChange(question.id, { ...dateValue, year: e.target.value })}
            />
          </div>
        );

      case 'scale':
        const scaleValue = responses[question.id];
        return (
          <div className="space-y-3">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>1 = très faible</span>
              <span>10 = très bon</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {Array.from({ length: (question.scaleMax || 10) - (question.scaleMin || 1) + 1 }, (_, i) => {
                const value = (question.scaleMin || 1) + i;
                const isSelected = scaleValue === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => handleScaleChange(question.id, value)}
                    className={`w-10 h-10 rounded-lg border-2 text-sm font-medium transition-all ${
                      isSelected 
                        ? 'bg-primary text-primary-foreground border-primary' 
                        : 'bg-background border-input hover:border-primary/50'
                    }`}
                  >
                    {value}
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 'conditionalText':
        const selectedValue = responses[question.id];
        const detailValue = responses[`${question.id}_detail`] || '';
        
        return (
          <div className="space-y-3">
            <RadioGroup
              value={selectedValue || ''}
              onValueChange={(value) => handleConditionalTextChange(question.id, value)}
            >
              {question.options?.map((option) => (
                <div key={option.id} className="flex items-center space-x-2">
                  <RadioGroupItem value={option.id} id={`${question.id}_${option.id}`} />
                  <Label htmlFor={`${question.id}_${option.id}`} className="cursor-pointer">
                    {option.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
            
            {selectedValue === 'oui' && question.conditionalTextLabel && (
              <div className="ml-6 mt-2">
                <Input
                  placeholder={question.conditionalTextLabel}
                  value={detailValue}
                  onChange={(e) => 
                    handleConditionalTextChange(question.id, selectedValue, e.target.value)
                  }
                />
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardContent className="pt-6 space-y-6">
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-primary">{config.title}</h3>
          <p className="text-sm text-muted-foreground italic">{config.intro}</p>
        </div>

        <div className="space-y-6">
          {config.questions.map((question) => {
            const rendered = renderQuestion(question);
            if (!rendered) return null;
            
            return (
              <div key={question.id} className="space-y-2">
                <Label className="text-sm font-medium">
                  {question.label}
                  <span className="text-destructive ml-1">*</span>
                </Label>
                {rendered}
              </div>
            );
          })}
        </div>

        {config.footer && (
          <div className="pt-4 border-t border-primary/20">
            <p className="text-xs text-muted-foreground italic bg-secondary/20 p-3 rounded-lg">
              💡 {config.footer}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Helper to validate questionnaire responses
export function validateQuestionnaireResponses(
  serviceTitle: string,
  responses: Record<string, any>
): { valid: boolean; missingFields: string[] } {
  const type = getQuestionnaireType(serviceTitle);
  const config = QUESTIONNAIRE_CONFIGS[type];
  const missingFields: string[] = [];

  for (const question of config.questions) {
    // Skip conditional "autre" free-text fields — only required when parent has "autre" selected
    if (question.id.endsWith('_autre')) {
      const parentId = question.id.replace('_autre', '');
      const parentValues = responses[parentId];
      const showField = Array.isArray(parentValues) && parentValues.includes('autre');
      if (!showField) continue;
      const v = responses[question.id];
      if (!v || (typeof v === 'string' && v.trim() === '')) {
        missingFields.push(question.label);
      }
      continue;
    }

    const value = responses[question.id];

    if (question.type === 'checkbox') {
      if (!value || !Array.isArray(value) || value.length === 0) {
        missingFields.push(question.label);
      }
    } else if (question.type === 'scale') {
      if (value === undefined || value === null) {
        missingFields.push(question.label);
      }
    } else if (question.type === 'date') {
      if (!value || !value.day || !value.month || !value.year) {
        missingFields.push(question.label);
      }
    } else if (question.type === 'text') {
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        missingFields.push(question.label);
      }
    } else if (question.type === 'conditionalText') {
      if (!value) {
        missingFields.push(question.label);
      } else if (value === 'oui' && question.conditionalTextLabel) {
        const detail = responses[`${question.id}_detail`];
        if (!detail || (typeof detail === 'string' && detail.trim() === '')) {
          missingFields.push(question.label);
        }
      }
    }
  }

  return {
    valid: missingFields.length === 0,
    missingFields,
  };
}
